import { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Calendar, Heart, Zap, Radio, MapPin, MessageCircle, X } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  event: Calendar,
  follow: Heart,
  mission: Zap,
  radio: Radio,
  general: Bell,
  update: MapPin,
  whatsapp: MessageCircle,
};

export default function NotificationBell({ token = '', forceVisible = false }) {
  const { isAuthenticated } = useAuth();
  const enabled = isAuthenticated || forceVisible;
  const requestConfig = useMemo(() => ({
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  }), [token]);
  const [unread, setUnread] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;

    const fetchCount = () => {
      axios.get(`${API}/api/notifications/unread-count`, requestConfig)
        .then(res => setUnread(res.data.count || 0))
        .catch(() => {});
    };

    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    return () => clearInterval(interval);
  }, [enabled, requestConfig]);

  useEffect(() => {
    if (!open || !enabled) return;
    axios.get(`${API}/api/notifications?limit=15`, requestConfig)
      .then(res => setNotifications(res.data.notifications || []))
      .catch(() => {});
  }, [enabled, open, requestConfig]);

  // Close on outside click
  useEffect(() => {
    const handle = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const markAllRead = async () => {
    await axios.put(`${API}/api/notifications/mark-all-read`, {}, requestConfig).catch(() => {});
    setUnread(0);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  if (!enabled) return null;

  return (
    <div className="relative" ref={ref} data-testid="notification-bell">
      <button
        onClick={() => setOpen(!open)}
        className="relative w-9 h-9 rounded-sm flex items-center justify-center bg-white/5 hover:bg-white/10 transition-colors"
        data-testid="notification-bell-btn"
      >
        <Bell className="w-5 h-5 text-muted-foreground" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-[10px] font-bold flex items-center justify-center text-white" data-testid="notification-count">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-12 w-80 sm:w-96 bg-card border border-white/10 rounded-sm shadow-xl z-50 max-h-[70vh] overflow-hidden flex flex-col" data-testid="notification-dropdown">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <h3 className="text-sm font-bold">Notifications</h3>
            <div className="flex items-center gap-2">
              {unread > 0 && (
                <button onClick={markAllRead} className="text-xs text-green-400 hover:underline" data-testid="mark-all-read">
                  Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* List */}
          <div className="overflow-y-auto flex-1">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-muted-foreground text-sm" data-testid="no-notifications">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-30" />
                No notifications yet
              </div>
            ) : (
              notifications.map(n => {
                const Icon = ICON_MAP[n.type] || Bell;
                return (
                  <Link
                    key={n.id}
                    to={n.link || '/following'}
                    onClick={() => setOpen(false)}
                    className={`px-4 py-3 border-b border-white/5 flex gap-3 transition-colors ${
                      !n.read ? 'bg-white/[0.03]' : ''
                    }`}
                    data-testid={`notification-${n.id}`}
                  >
                    <div className={`w-8 h-8 rounded-sm flex items-center justify-center flex-shrink-0 ${!n.read ? 'bg-green-500/10' : 'bg-white/5'}`}>
                      <Icon className={`w-4 h-4 ${!n.read ? 'text-green-400' : 'text-muted-foreground'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${!n.read ? 'font-semibold' : 'text-muted-foreground'}`}>{n.title}</p>
                      <p className="text-xs text-muted-foreground truncate">{n.message}</p>
                      {n.created_at && (
                        <p className="text-[10px] text-muted-foreground mt-1">
                          {formatTimeAgo(n.created_at)}
                        </p>
                      )}
                    </div>
                    {!n.read && <div className="w-2 h-2 rounded-full bg-green-500 mt-1 flex-shrink-0" />}
                  </Link>
                );
              })
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-white/10 text-center">
              <Link to={forceVisible ? '/admin/meta-whatsapp/inbox' : '/following'} onClick={() => setOpen(false)} className="text-xs text-green-400 hover:underline">
                {forceVisible ? 'Open WhatsApp inbox' : 'View following & activity'}
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function formatTimeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
